// Pulumi error blobs from DCM are long, dense, and hard to act on. This
// classifier looks for known patterns and returns a structured summary with a
// suggested fix. Returns null when no rule matches; callers should render the
// raw text in that case.

import { match, P } from 'ts-pattern';

export type FriendlyError = {
  title: string;
  description: string;
  suggestion: string;
  raw: string;
};

// Order matters: most specific patterns first, generic fallback last.
// The Snowflake syntax-error + unexpected-token shape is what's emitted for
// invalid identifiers (hyphens, leading digits, etc.). The bare 001003 (42000)
// code covers many unrelated compilation errors and would mis-title those.
export function friendlyPulumiError( raw: string | null | undefined ): FriendlyError | null {
  return match( raw )
    .with( P.nullish, () => null )
    .with( P.string.regex( /SQL compilation error[\s\S]*?syntax error[\s\S]*?unexpected/i ), ( text ) => ( {
      title: 'Snowflake rejected an invalid identifier',
      description: 'Snowflake refused to compile the SQL because an object name (database, schema, table, or column) contains characters that aren\'t allowed in unquoted identifiers — most often a hyphen, leading digit, or other special character.',
      suggestion: 'Check your contract\'s `tenant`, `schema`, and column names. Replace hyphens (-) with underscores (_), make sure each name starts with a letter, and avoid spaces or special characters. The error message includes a position that points at the offending character.',
      raw: text
    } ) )
    .with( P.string.regex( /Error inferring schema from files/i ), ( text ) => ( {
      title: 'Airbyte couldn\'t read your source files',
      description: 'Airbyte\'s connector failed to infer a schema from the configured source files — they were unreachable, in an unexpected format, or empty.',
      suggestion: 'Verify the path/glob in your contract\'s `servers.source` block points to readable files in the expected format (CSV, JSONL, etc.) with consistent columns. Check file encoding and try sampling one record to confirm it parses.',
      raw: text
    } ) )
    .with( P.string.regex( /failed to get token: failed to decode token response: invalid character '<'/i ), ( text ) => ( {
      title: 'Airbyte returned an HTML error page during auth',
      description: 'When DCM tried to authenticate against the Airbyte API, the response was an HTML page (typically a 502/503 from a proxy) instead of a JSON token — meaning the Airbyte server was unreachable or returned an error page.',
      suggestion: 'Confirm the Airbyte server is up (check the `dcm-airbyte-server` pod). If you\'re running locally, ensure the URL in `airbyte.global` config and the API client ID/secret match the live deployment.',
      raw: text
    } ) )
    .with( P.string.regex( /Pulumi (up|destroy|preview) failed/i ), ( text ) => ( {
      title: 'Infrastructure provisioning failed',
      description: 'Pulumi could not complete the resource update for this contract.',
      suggestion: 'Expand the full error log below and look for lines starting with `error:` to find the failing resource. The provider name (snowflake, airbyte, etc.) on that line tells you which system to investigate.',
      raw: text
    } ) )
    .otherwise( () => null );
}
